import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useParams } from 'react-router';
import type { GetCommentParams } from '@/api/comments';
import { getRoverMilestone } from '@/api/Rover/applicationTDM';
import { getClientApplicationComments, postClientApplicationComments } from '@/api/Rover/clientApplication';
import Comments from '@/components/Comments';
import Button from '@/components/UI/Button';
import LoadingTdmDetail from '@/components/UI/loading/LoadingTdmDetail';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
import TdmAttachments from '../Tdm/TdmAttachments';
import TdmDescription from '../Tdm/TdmDescription';

export default function RoverMilestone() {
  const params = useParams<string>();

  //Tanstack: version 5 query implementation
  const fetchMilestone = useQuery({
    queryKey: [RoverReactQueryKey.GET_ROVER_MILESTONES, params],
    queryFn: () => getRoverMilestone(Number(params.milestoneId)),
  });
  const milestone = fetchMilestone.data?.data;

  const getComments = useCallback(
    (_params: GetCommentParams) => {
      return getClientApplicationComments({
        params: {
          resource_id: milestone.id.toString(),
          resource_type: 'TDMRover',
        },
      });
    },
    [milestone]
  );

  const postComments = useCallback(
    async (values: { comment: string }) => {
      const response = await postClientApplicationComments({
        data: {
          resource_id: milestone.id.toString(),
          resource_type: 'TDMRover',
          ...values,
        },
      });
      return response;
    },
    [milestone]
  );

  const TdmDetailError = (
    <div className='flex w-full h-screen/2 justify-center items-center'>
      <div className='flex flex-col justify-center items-center h-full'>
        <div className='flex flex-col bg-white px-5 py-6 rounded-md max-w-xl text-center justify-center items-center shadow-lg'>
          <div className='bg-red-50 p-4 rounded-full my-4'>
            <ExclamationCircleIcon className='h-10 w-10 text-tertiaryColor-normal' />
          </div>
          <h1 className='m-0'>Uh oh! something went wrong</h1>
          <p className='mt-2 mb-5'>
            Sorry, We are currently unable to fetch requested milestone from the server. Please try again or If this problem persist, report
            an issue to support@sein.tech
          </p>
          <div>
            <Button label='Reload page' btnStyle='btn-primary' type='button' onClick={() => window.location.reload()} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div id='milestone-details'>
      <h3 className='text-3xl font-extrabold m-0 capitalize'>Milestone Detail</h3>
      {fetchMilestone.isError && TdmDetailError}
      <main className='w-full'>
        {fetchMilestone.isLoading && <LoadingTdmDetail />}
        {fetchMilestone.isSuccess && (
          <div className='mt-8 w-full mx-auto grid grid-cols-1 gap-6 lg:grid-flow-col-dense xl:grid-cols-3'>
            <div className='space-y-6 xl:col-start-1 xl:col-span-2'>
              <TdmDescription tdm={milestone!} tdmType='milestone' />

              <div className='block xl:hidden'>
                <TdmAttachments tdm={milestone!} tdmType='milestone' />
              </div>
              <Comments getComments={getComments} postComments={postComments} queryKey='rover-task' />
            </div>
            <div className='space-y-6 hidden xl:block xl:col-span-1'>
              <TdmAttachments tdm={milestone!} tdmType='milestone' />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
